/**
 * scorecard.ts — the scoring reconciliation (GUI v2 req G.5).
 *
 * 18_call-scorecard.md is the CANONICAL scoring engine: seven weighted
 * categories (20/20/15/10/10/15/10) with four automatic-fail conditions. The
 * four-item live checklist (20/20/20/40) stays the in-call display. Both are
 * computed from the SAME underlying detections (BehaviorKey set) — mapped
 * here. The weights intentionally differ (display emphasis vs canonical
 * grade); the mapping + that judgment are documented for Phil in
 * docs/LIVE_COACH_GUI2_2026-06-11.md § Scoring reconciliation.
 *
 * The wrap-up scorecard rendered from live detections is PROVISIONAL — the
 * authoritative grade remains the after-call AI scoring pipeline. Live
 * detections can observe that a behavior HAPPENED, not how well; provisional
 * banding below is coverage-based (observed core behaviors / expected).
 */
import type { BehaviorKey, CriterionKey } from "@/types";

export interface ScoreCategory {
  key: string;
  label: string;
  /** 18 §2 weights (sum 100). */
  weight: number;
  stages: string;
  /** Core behaviors that evidence this category (shared detection set). */
  behaviors: BehaviorKey[];
  /** Category applies only when its moment occurred (18 §3 "Not Observed"). */
  conditionalOn: "objection" | "price" | null;
}

/** Source: 18_call-scorecard.md §2 (weights) + §4 (definitions). */
export const SCORE_CATEGORIES: ScoreCategory[] = [
  {
    key: "discovery",
    label: "Discovery",
    weight: 20,
    stages: "4–6",
    behaviors: ["product_path_clarified", "why_now_asked", "implication_question_asked", "motivation_captured", "gap_confirmed"],
    conditionalOn: null,
  },
  {
    key: "product_fit",
    label: "Product Fit / Recommendation",
    weight: 20,
    stages: "7",
    behaviors: ["education_moment", "recommendation_made", "premium_offered"],
    conditionalOn: null,
  },
  {
    key: "value_building",
    label: "Value Building",
    weight: 15,
    stages: "8",
    behaviors: ["value_tied_to_reason", "warranty_named", "exclusivity_claimed"],
    conditionalOn: null,
  },
  {
    key: "price_confidence",
    label: "Price Confidence",
    weight: 10,
    stages: "9",
    behaviors: ["price_stated", "deposit_math_mentioned", "prepay_disclosed_over_1000"],
    conditionalOn: "price",
  },
  {
    key: "concern_handling",
    label: "Concern Handling",
    weight: 10,
    stages: "10",
    behaviors: ["objection_acknowledged", "objection_clarified", "next_step_asked_after_objection"],
    conditionalOn: "objection",
  },
  {
    key: "close_best_outcome",
    label: "Close to Best Outcome",
    weight: 15,
    stages: "11",
    behaviors: ["booking_asked", "two_times_offered", "deposit_asked", "showroom_invited", "followup_set"],
    conditionalOn: null,
  },
  {
    key: "crm_capture",
    label: "CRM Capture",
    weight: 10,
    stages: "2, 12",
    behaviors: ["name_captured", "phone_captured", "vehicle_captured", "readback_done", "followup_set"],
    conditionalOn: null,
  },
];

/**
 * 4-item live checklist ← shared detections (req G.5). The "captured" item is
 * card-driven (name+vehicle+reason sub-checks); these are the behavior triggers
 * for the other three.
 */
export const CHECKLIST_BEHAVIOR_MAP: Record<CriterionKey, BehaviorKey[]> = {
  captured_customer_vehicle_reasons: ["name_captured", "vehicle_captured", "motivation_captured"],
  built_value_usp: ["education_moment", "value_tied_to_reason", "warranty_named", "exclusivity_claimed"],
  offered_premium: ["premium_offered"],
  confirm_booking: ["booking_confirmed"],
};

/** Coverage ratio → 18 §3 levels (provisional only — see header). */
export function provisionalLevel(ratio: number): { label: string; points: number } {
  if (ratio >= 0.99) return { label: "Excellent", points: 100 };
  if (ratio >= 0.6) return { label: "Good", points: 80 };
  if (ratio >= 0.3) return { label: "Average", points: 50 };
  return { label: "Poor", points: 0 };
}

export interface ProvisionalCategoryScore {
  category: ScoreCategory;
  observed: BehaviorKey[];
  notObserved: boolean;
  label: string;
  points: number;
  weighted: number;
}

export interface ProvisionalScorecard {
  categories: ProvisionalCategoryScore[];
  /** Weighted total, reweighted around Not Observed categories (18 §3). */
  total: number;
}

export function computeProvisionalScorecard(
  behaviors: Set<BehaviorKey>,
  context: { objectionRaised: boolean; priceStated: boolean },
): ProvisionalScorecard {
  const categories = SCORE_CATEGORIES.map((category) => {
    const notObserved =
      (category.conditionalOn === "objection" && !context.objectionRaised) ||
      (category.conditionalOn === "price" && !context.priceStated);
    const observed = category.behaviors.filter((b) => behaviors.has(b));
    const ratio = category.behaviors.length === 0 ? 0 : observed.length / category.behaviors.length;
    const level = provisionalLevel(ratio);
    return {
      category,
      observed,
      notObserved,
      label: notObserved ? "Not observed" : level.label,
      points: notObserved ? 0 : level.points,
      weighted: notObserved ? 0 : (level.points * category.weight) / 100,
    };
  });
  const activeWeight = categories.reduce((sum, c) => sum + (c.notObserved ? 0 : c.category.weight), 0);
  const rawTotal = categories.reduce((sum, c) => sum + c.weighted, 0);
  // Reweight proportionally when categories were Not Observed (18 §3).
  const total = activeWeight > 0 ? Math.round((rawTotal / activeWeight) * 100) : 0;
  return { categories, total };
}

/** Auto-fail copy (18 §5) for the wrap-up + the live red alert. */
export const AUTO_FAILS: Record<string, { label: string; detail: string }> = {
  no_crm_minimums: {
    label: "Pricing without CRM minimums",
    detail: "Price or recommendation given, but name, phone, vehicle, or service interest is missing.",
  },
  no_close_attempt: {
    label: "No close attempted",
    detail: "Call ended with no attempt at any outcome in the hierarchy. “Just let us know” endings fall here.",
  },
  prohibited_claim: {
    label: "Prohibited claim",
    detail: "MPG figure, fuel dollar amount, pays-for-itself, coating-as-chip-protection, flat TSER, or off-sheet number.",
  },
  improvised_legal: {
    label: "Improvised legal advice",
    detail: "Any legal statement beyond Tint King's official guidance.",
  },
};
